import { useEffect } from "react";
import { useInRouterContext, useLocation } from "react-router-dom";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { useNotices, dismiss, onRouteChange, registerPrimaryMount } from "@/lib/notices";

/**
 * Feedback, shown in the page. Sits at the top of the content column and
 * sticks to the top of the viewport while the page scrolls, so a "Saved"
 * raised by a button at the bottom of a long form is still seen — but it
 * is part of the page frame, not a card floating over it.
 *
 * `className` carries the sticky offset and any gutter the mount point
 * needs (the app shell's content column has a mobile header above it).
 *
 * `fallback`: the copy mounted in the app root. It only renders while no
 * page has mounted its own, so a notice is never shown twice and never lost.
 */
const STYLE = {
  success: { role: "status", icon: CheckCircle2, box: "bg-emerald-50 border-emerald-200 text-emerald-900", icn: "text-emerald-600" },
  info:    { role: "status", icon: Info,         box: "bg-sky-50 border-sky-200 text-sky-900",             icn: "text-sky-600" },
  warning: { role: "status", icon: AlertTriangle, box: "bg-amber-50 border-amber-200 text-amber-900",      icn: "text-amber-600" },
  error:   { role: "alert",  icon: AlertCircle,  box: "bg-rose-50 border-rose-200 text-rose-900",          icn: "text-rose-600" },
};

export default function NoticeBar({ fallback = false, className = "" }) {
  const { notices, primaryMounts } = useNotices();
  const inRouter = useInRouterContext();

  useEffect(() => {
    if (fallback) return undefined;
    return registerPrimaryMount();
  }, [fallback]);

  if (fallback && primaryMounts > 0) return null;
  return (
    <>
      {inRouter && <RouteWatcher />}
      {notices.length > 0 && (
        <div
          className={`sticky z-30 py-2 bg-paper/95 backdrop-blur-sm space-y-2 ${className}`}
          data-testid="notice-bar"
        >
          {notices.map((n) => <Notice key={n.id} notice={n} />)}
        </div>
      )}
    </>
  );
}

/** Hooks cannot be called conditionally; this child exists only inside a router. */
function RouteWatcher() {
  const { pathname } = useLocation();
  useEffect(() => { onRouteChange(); }, [pathname]);
  return null;
}

function Notice({ notice }) {
  const s = STYLE[notice.kind] || STYLE.info;
  const Icon = s.icon;
  return (
    <div
      role={s.role}
      data-testid="notice"
      data-kind={notice.kind}
      className={`flex items-start gap-3 rounded-sm border px-3 py-2.5 text-sm ${s.box}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.icn}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="font-medium">{notice.message}</div>
        {notice.description && <div className="mt-0.5 text-[13px] opacity-80">{notice.description}</div>}
      </div>
      {notice.action && (
        <button
          type="button"
          onClick={() => { notice.action.onClick?.(); dismiss(notice.id); }}
          className="shrink-0 text-sm font-semibold underline underline-offset-2 hover:opacity-80"
        >
          {notice.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => dismiss(notice.id)}
        aria-label="Dismiss"
        className="shrink-0 -mr-1 p-1 rounded-sm opacity-60 hover:opacity-100 hover:bg-black/5"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
